"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    LiveKitRoom,
    RoomAudioRenderer,
    useConnectionState,
    useLocalParticipant,
    useRemoteParticipants,
    useTracks,
} from "@livekit/components-react";
import { ConnectionState, Track } from "livekit-client";
import { Mic, MicOff, PhoneOff, Phone, X, MessageSquare, ChevronDown } from "lucide-react";
import gsap from "gsap";
import { AgentAudioVisualizerAura } from "@/components/agent-audio-visualizer-aura";

interface FloatingVoiceAgentProps {
    agentName?: string;
    displayName?: string;
    wsUrl?: string;
    tokenEndpoint?: string;
}

interface TokenResponse {
    token: string;
    url: string;
    agentName: string;
    roomName: string;
}



/* ──────────────────────────────────────────────────────────────────
   Agent Content – Active UI
   ────────────────────────────────────────────────────────────── */

function AgentContent({
    agentName,
    onDisconnect,
}: {
    agentName: string;
    onDisconnect: () => void;
}) {
    const { t } = useTranslation();
    const connectionState = useConnectionState();
    const { localParticipant } = useLocalParticipant();
    const remoteParticipants = useRemoteParticipants();
    const [isMuted, setIsMuted] = useState(false);
    const [audioLevel, setAudioLevel] = useState(0);
    const [agentState, setAgentState] = useState<
        "idle" | "listening" | "thinking" | "speaking"
    >("idle");

    const audioTracks = useTracks([Track.Source.Microphone]);

    const agentTrack = audioTracks.find(
        (t) => t.participant.isAgent || (remoteParticipants.length > 0 && remoteParticipants.some(rp => rp.identity === t.participant.identity))
    );

    useEffect(() => {
        if (!agentTrack?.publication?.track) return;

        const interval = setInterval(() => {
            // Use the participant's audio level if available
            const participant = agentTrack.participant;
            if (participant && participant.audioLevel !== undefined) {
                const level = participant.audioLevel;
                setAudioLevel(level);
                if (level > 0.01) {
                    setAgentState("speaking");
                } else if (connectionState === ConnectionState.Connected) {
                    setAgentState("listening");
                }
            }
        }, 50);

        return () => clearInterval(interval);
    }, [agentTrack, connectionState]);

    useEffect(() => {
        if (connectionState === ConnectionState.Connected && remoteParticipants.length === 0) {
            setAgentState("thinking");
        } else if (connectionState === ConnectionState.Connected && audioLevel < 0.01) {
            setAgentState("listening");
        }
    }, [connectionState, remoteParticipants.length, audioLevel]);

    const toggleMute = useCallback(() => {
        if (localParticipant) {
            const newMuted = !isMuted;
            localParticipant.setMicrophoneEnabled(!newMuted);
            setIsMuted(newMuted);
        }
    }, [localParticipant, isMuted]);

    const stateLabels: Record<string, string> = {
        idle: t("VoiceAgent.ready"),
        listening: t("VoiceAgent.listening"),
        thinking: t("VoiceAgent.thinking"),
        speaking: t("VoiceAgent.speaking"),
    };

    const stateColors: Record<string, string> = {
        idle: "text-cyan-800",
        listening: "text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.8)]",
        thinking: "text-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.8)]",
        speaking: "text-blue-300 drop-shadow-[0_0_8px_rgba(147,197,253,0.8)]",
    };

    return (
        <div className="flex flex-col items-center gap-6 p-6 pb-8 bg-cyan-950/20 backdrop-blur-xl border-t border-cyan-500/20 rounded-b-3xl">
            <div className="relative flex items-center justify-center">
                <AgentAudioVisualizerAura
                    audioTrack={agentTrack}
                    state={agentState === "idle" ? "connecting" : agentState}
                    size="lg"
                    color="#06b6d4"
                    colorShift={0.1}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center backdrop-blur-sm transition-all duration-500 ${isMuted ? "bg-red-500/20 border border-red-500/30" : "bg-white/5 border border-white/10"
                        }`}>
                        {isMuted ? <MicOff className="w-5 h-5 text-red-400" /> : <Mic className="w-5 h-5 text-white/80" />}
                    </div>
                </div>
            </div>

            <div className="text-center space-y-1">
                <h2 className="text-lg font-bold text-white leading-tight">{agentName}</h2>
                <p className={`text-xs font-bold uppercase tracking-widest ${stateColors[agentState]}`}>
                    {stateLabels[agentState]}
                </p>
            </div>

            <div className="flex items-center gap-3">
                <button
                    onClick={toggleMute}
                    className={`p-3 rounded-full transition-all duration-300 ${isMuted ? "bg-red-500/10 hover:bg-red-500/20 border border-red-500/30" : "bg-white/5 hover:bg-white/10 border border-white/10"
                        }`}
                >
                    {isMuted ? <MicOff className="w-4 h-4 text-red-400" /> : <Mic className="w-4 h-4 text-white/70" />}
                </button>

                <button
                    onClick={onDisconnect}
                    className="p-3 rounded-full bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 transition-all duration-300"
                >
                    <PhoneOff className="w-4 h-4 text-red-400" />
                </button>
            </div>
        </div>
    );
}

/* ──────────────────────────────────────────────────────────────────
   Floating AI Agent Root
   ────────────────────────────────────────────────────────────── */

export function FloatingVoiceAgent({
    agentName,
    displayName,
    wsUrl,
    tokenEndpoint = "/api/livekit-token",
}: FloatingVoiceAgentProps) {
    const { t } = useTranslation();
    // Falls back to NEXT_PUBLIC env if available, or a hardcoded default that worked before
    const [wsUrlState, setWsUrlState] = useState(wsUrl || process.env.NEXT_PUBLIC_LIVEKIT_URL || "");
    const label = displayName || agentName || t("VoiceAgent.title");
    const [isOpen, setIsOpen] = useState(false);
    const [isRendered, setIsRendered] = useState(false);
    const [isSessionActive, setIsSessionActive] = useState(false);
    const [token, setToken] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const popupRef = useRef<HTMLDivElement>(null);
    const btnRef = useRef<HTMLButtonElement>(null);
    const iconCloseRef = useRef<HTMLDivElement>(null);
    const iconOpenRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isOpen) {
            setIsRendered(true);
            if (iconOpenRef.current && iconCloseRef.current) {
                gsap.to(iconOpenRef.current, { rotation: -90, opacity: 0, duration: 0.3, ease: "power2.inOut", overwrite: true });
                gsap.fromTo(iconCloseRef.current,
                    { rotation: -90, opacity: 0 },
                    { rotation: 0, opacity: 1, duration: 0.3, ease: "power2.inOut", overwrite: true }
                );
            }
        } else {
            if (popupRef.current) {
                gsap.to(popupRef.current, {
                    opacity: 0, y: 20, scale: 0.95, filter: "blur(10px)",
                    duration: 0.3, ease: "power2.in",
                    onComplete: () => setIsRendered(false)
                });
            } else {
                setIsRendered(false);
            }
            if (iconOpenRef.current && iconCloseRef.current) {
                gsap.to(iconCloseRef.current, { rotation: 90, opacity: 0, duration: 0.3, ease: "power2.inOut", overwrite: true });
                gsap.fromTo(iconOpenRef.current,
                    { rotation: 90, opacity: 0 },
                    { rotation: 0, opacity: 1, duration: 0.3, ease: "power2.inOut", overwrite: true }
                );
            }
        }
    }, [isOpen]);

    useEffect(() => {
        if (isRendered && isOpen && popupRef.current) {
            gsap.fromTo(popupRef.current,
                { opacity: 0, y: 20, scale: 0.95, filter: "blur(10px)", display: "none" },
                { opacity: 1, y: 0, scale: 1, filter: "blur(0px)", display: "block", duration: 0.3, ease: "power2.out" }
            );
        }
    }, [isRendered, isOpen]);

    const startSession = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const url = agentName ? `${tokenEndpoint}?agentName=${encodeURIComponent(agentName)}` : tokenEndpoint;
            const res = await fetch(url);
            if (!res.ok) throw new Error("Connection failed");
            const data: TokenResponse = await res.json();

            if (data.url) setWsUrlState(data.url);
            setToken(data.token);
            setIsSessionActive(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to start");
        } finally {
            setIsLoading(false);
        }
    };

    const endSession = () => {
        setIsSessionActive(false);
        setToken("");
    };

    return (
        <div className="fixed bottom-6 left-6 md:bottom-10 md:left-10 z-[100] flex flex-col items-start gap-4">
            {isRendered && (
                <div
                    ref={popupRef}
                    className="w-[300px] bg-[#050510]/95 backdrop-blur-2xl rounded-[2.5rem] border border-cyan-500/40 shadow-[0_0_40px_rgba(6,182,212,0.3)] overflow-hidden"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-5 border-b border-cyan-500/20">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shadow-[0_0_15px_rgba(6,182,212,0.4)]">
                                <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse shadow-[0_0_8px_rgba(34,211,238,1)]" />
                            </div>
                            <span className="text-sm font-black tracking-widest uppercase text-cyan-100/90">{t("VoiceAgent.title")}</span>
                        </div>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="p-2 hover:bg-white/5 rounded-full transition-colors"
                        >
                            <ChevronDown className="w-4 h-4 text-white/40" />
                        </button>
                    </div>

                    {/* Content Area */}
                    {!isSessionActive ? (
                        <div className="p-8 flex flex-col items-center gap-6 text-center">
                            <div className="relative">
                                <AgentAudioVisualizerAura audioTrack={undefined} state="connecting" size="lg" color="#06b6d4" />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <Mic className="w-6 h-6 text-cyan-400" />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <h3 className="text-lg font-bold text-cyan-50 drop-shadow-[0_0_10px_rgba(6,182,212,0.4)]">{t("VoiceAgent.greeting")}</h3>
                                <p className="text-xs text-cyan-100/50 leading-relaxed px-4">
                                    {t("VoiceAgent.description")}
                                </p>
                            </div>

                            <button
                                onClick={startSession}
                                disabled={isLoading}
                                className="w-full py-4 rounded-2xl bg-cyan-600/20 hover:bg-cyan-500/40 border border-cyan-500/50 text-cyan-50 font-bold tracking-wider uppercase text-xs transition-all duration-300 shadow-[0_0_20px_rgba(6,182,212,0.2)] disabled:opacity-50"
                            >
                                {isLoading ? t("VoiceAgent.connecting") : t("VoiceAgent.startChat")}
                            </button>

                            {error && <p className="text-[10px] text-red-400 italic">{error}</p>}
                        </div>
                    ) : (
                        <LiveKitRoom
                            token={token}
                            serverUrl={wsUrlState}
                            connect={true}
                            audio={true}
                            video={false}
                            options={{
                                dynacast: true,
                            }}
                            onDisconnected={endSession}
                            className="w-full"
                        >
                            <AgentContent agentName={label} onDisconnect={endSession} />
                            <RoomAudioRenderer />
                        </LiveKitRoom>
                    )}
                </div>
            )}

            {/* Launcher Button */}
            <button
                ref={btnRef}
                aria-label="Toggle AI Voice Assistant"
                onClick={() => setIsOpen(!isOpen)}
                onMouseEnter={() => gsap.to(btnRef.current, { scale: 1.05, duration: 0.2 })}
                onMouseLeave={() => gsap.to(btnRef.current, { scale: 1, duration: 0.2 })}
                onMouseDown={() => gsap.to(btnRef.current, { scale: 0.95, duration: 0.1 })}
                onMouseUp={() => gsap.to(btnRef.current, { scale: 1.05, duration: 0.1 })}
                className={`relative group w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center transition-all duration-500 ${isOpen ? "bg-black text-cyan-400 border border-cyan-500/50 shadow-[0_0_30px_rgba(6,182,212,0.3)]" : "bg-cyan-950/80 backdrop-blur-xl border border-cyan-400/50 text-cyan-300 shadow-[0_0_20px_rgba(6,182,212,0.5)]"
                    }`}
            >
                <div ref={iconCloseRef} className="absolute inset-0 flex items-center justify-center opacity-0">
                    <X className="w-6 h-6" />
                </div>

                <div ref={iconOpenRef} className="absolute inset-0 flex items-center justify-center">
                    <div className="relative">
                        <MessageSquare className="w-6 h-6" />
                        {!isSessionActive && (
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-cyan-400 rounded-full border-2 border-black animate-pulse shadow-[0_0_10px_rgba(34,211,238,1)]" />
                        )}
                    </div>
                </div>

                {/* Glow effect when active */}
                {isSessionActive && !isOpen && (
                    <div className="absolute inset-0 rounded-full bg-cyan-500/40 animate-ping" />
                )}
            </button>
        </div>
    );
}
